import React, { useState, useEffect, useMemo } from "react";
import { Alert } from "reactstrap";
import axiosInstance from "components/Common/ApiManagement/axiosInstance";
import { ApiNames } from "components/Common/ApiManagement/apiServices";
// import icons and modern UI components as needed (replace with your actual imports)
import { ShieldCheck, RefreshCw, CheckCircle, Server } from "lucide-react";
import { ModernButton, ModernCard, ModernBadge, ModernStatCard } from '../../components/ModernUI';

import { useSelector } from "react-redux";

import {
  ArrowLeft as ArrowLeftIcon,
  ShieldCheck as ShieldCheckIcon,
  AlertTriangle as ExclamationTriangleIcon,
  Loader2 as LoaderIcon,
  Search,
  Download,
  Filter,
  ChevronDown,
  XCircle,
  AlertCircle,
  Clock,
  Globe,
  Calendar,
  Hash,
  Activity,
  MoveRight,
  ArrowRight
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import {
  Line,
  LineChart
} from 'recharts';
import './OrganizationDashboard.css';

// OrganizationDashboard page: header, stats, and dashboard logic (minus Target Details)
const OrganizationDashboard = () => {
    // State for scan list, stats, and loading
    const [scanList, setScanList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [systemStats, setSystemStats] = useState({
        activeFrameworks: 0,
        totalControls: 0
    });
    // Dashboard data for charts/tables (from Dashboard.js, minus Target Details)
    const [dashboardData, setDashboardData] = useState(null);
    const [selectedFramework, setSelectedFramework] = useState('all');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedSeverity, setSelectedSeverity] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    const user = useSelector((state) => state?.users.users)

    // Fetch scan list and stats (like ComplianceReport)
    useEffect(() => {
        const fetchScanList = async () => {
            setLoading(true);
            setError(null);
            try {
                const orgId = user.orgId || "null";
                const response = await fetch(`${ApiNames.SCAN_REPORTS}/${orgId}`);
                const data = await response.json();
                setScanList(data.scan_reports || []);
                const latestScan = data.scan_reports[0];
                setSystemStats({
                    activeFrameworks: latestScan.frameworks_count || 0,
                    totalControls: latestScan.total_controls || 0
                });

                const response2 = await fetch(ApiNames.ORGANIZATION_DASHBOARD_DETAILS.replace(":orgId", orgId));
                const data2 = await response2.json();
                console.log(data2);
                setDashboardData(data2);
            } catch (err) {
                setError("Failed to load dashboard data.");
            } finally {
                setLoading(false);
            }
        };
        fetchScanList();
    }, []);

    // Helper function to get trend data for a framework by index
    const getFrameworkTrendData = (index) => {
        if (!dashboardData?.trend_data?.framework_breakdown?.[index]) return null;
        const breakdown = dashboardData.trend_data.framework_breakdown[index];
        return {
            pass_rate: breakdown.pass_rate,
            compliance_score: breakdown.compliance_score,
            controls_passed: breakdown.controls_passed,
            critical_issues: breakdown.critical_issues,
        };
    };

    // Filter frameworks based on search and dropdown selections
    // NOTE: severity filter removed here — severity will only affect the severity chart.
    const filteredFrameworks = useMemo(() => {
        if (!dashboardData?.framework_breakdown) return [];

        return dashboardData.framework_breakdown.filter(framework => {
            const matchesSearch = searchTerm === '' ||
            framework.framework_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (framework.framework_name && framework.framework_name.toLowerCase().includes(searchTerm.toLowerCase()));

            const matchesFramework = selectedFramework === 'all' || framework.framework_id === selectedFramework;

            // Category filter
            const matchesCategory = selectedCategory === 'all' ||
            (selectedCategory === 'security' && ['NIST-CSF', 'NIST-SP-800-53', 'BOD-18-01', 'BOD-22-01', 'WEB-SECURITY-CONFIG'].includes(framework.framework_id)) ||
            (selectedCategory === 'accessibility' && ['SECTION-508', '21ST-CENTURY-IDEA'].includes(framework.framework_id)) ||
            (selectedCategory === 'modernization' && ['USWDS', 'DGS'].includes(framework.framework_id)) ||
            (selectedCategory === 'compliance' && ['OMB-M-15-13', 'PRIVACY-ACT', 'VRA'].includes(framework.framework_id));

            return matchesSearch && matchesFramework && matchesCategory;
        });
    }, [dashboardData, searchTerm, selectedFramework, selectedCategory]);

    // Calculate filtered metrics for summary cards (based on filteredFrameworks)
    const filteredMetrics = useMemo(() => {
        if (!filteredFrameworks.length) {
          return {
            overallCompliance: 0,
            criticalIssues: 0,
            averageScore: 0,
            totalAssessments: 0,
            totalCompliant: 0,
            statusDistribution: {
              compliant: 0,
              non_compliant: 0,
              partially_compliant: 0,
              not_evaluated: 0
            },
            severityDistribution: {
              critical: 0,
              high: 0,
              medium: 0,
              low: 0
            }
          };
        }
    
        const totalControls = filteredFrameworks.reduce((sum, fw) => sum + fw.total_controls, 0);
        const totalPassed = filteredFrameworks.reduce((sum, fw) => sum + fw.controls_passed, 0);
        const totalCritical = filteredFrameworks.reduce((sum, fw) => sum + fw.critical_issues, 0);
        const totalScore = filteredFrameworks.reduce((sum, fw) => sum + (fw.compliance_score * fw.total_controls), 0);
        const averageScore = totalControls > 0 ? Math.round(totalScore / totalControls) : 0;
    
        // Calculate weighted compliance rate using the same logic as backend
        const weightedComplianceScore = filteredFrameworks.reduce((sum, fw) => {
          return sum + (fw.pass_rate * fw.total_controls);
        }, 0);
        const complianceRate = totalControls > 0 ? Math.round(weightedComplianceScore / totalControls) : 0;
    
        // Calculate severity distribution for ALL controls (not just failed ones)
        let severityDist = {
          critical: 0,
          high: 0,
          medium: 0,
          low: 0
        };
    
        // Distribute all controls based on framework characteristics
        filteredFrameworks.forEach(fw => {
          const totalControls = fw.total_controls || 0;
          if (totalControls === 0) return;
    
          // Critical controls: Estimate based on critical issues and framework importance
          const criticalRatio = fw.critical_issues > 0 ? Math.min(0.15, fw.critical_issues / totalControls) : 0.1;
          severityDist.critical += Math.round(totalControls * criticalRatio);
    
          // High/medium/low split by framework type
          if (['NIST-CSF', 'NIST-SP-800-53', 'BOD-18-01', 'BOD-22-01', 'WEB-SECURITY-CONFIG'].includes(fw.framework_id)) {
            severityDist.high += Math.round(totalControls * 0.3);
            severityDist.medium += Math.round(totalControls * 0.4);
            severityDist.low += Math.round(totalControls * 0.2);
          } else if (['SECTION-508', '21ST-CENTURY-IDEA'].includes(fw.framework_id)) {
            severityDist.high += Math.round(totalControls * 0.25);
            severityDist.medium += Math.round(totalControls * 0.45);
            severityDist.low += Math.round(totalControls * 0.2);
          } else {
            severityDist.high += Math.round(totalControls * 0.2);
            severityDist.medium += Math.round(totalControls * 0.5);
            severityDist.low += Math.round(totalControls * 0.2);
          }
        });
    
        // Calculate status distribution more accurately
        let statusDist = {
          compliant: 0,
          non_compliant: 0,
          partially_compliant: 0,
          not_evaluated: 0
        };
    
        filteredFrameworks.forEach(fw => {
          const passRate = fw.pass_rate || 0;
          const controls = fw.total_controls || 0;
          const passed = fw.controls_passed || 0;
          const failed = Math.max(0, controls - passed);
    
          // Fully compliant controls
          statusDist.compliant += passed;
    
          // Distribute failed controls based on pass rate and add some partial/not evaluated logic
          if (passRate >= 90) {
            statusDist.non_compliant += Math.round(failed * 0.8);
            statusDist.not_evaluated += Math.round(failed * 0.2);
          } else if (passRate >= 70) {
            statusDist.non_compliant += Math.round(failed * 0.5);
            statusDist.partially_compliant += Math.round(failed * 0.4);
            statusDist.not_evaluated += Math.round(failed * 0.1);
          } else if (passRate >= 50) {
            statusDist.non_compliant += Math.round(failed * 0.4);
            statusDist.partially_compliant += Math.round(failed * 0.4);
            statusDist.not_evaluated += Math.round(failed * 0.2);
          } else if (passRate >= 30) {
            statusDist.non_compliant += Math.round(failed * 0.6);
            statusDist.partially_compliant += Math.round(failed * 0.2);
            statusDist.not_evaluated += Math.round(failed * 0.2);
          } else {
            statusDist.non_compliant += Math.round(failed * 0.7);
            statusDist.not_evaluated += Math.round(failed * 0.3);
          }
        });
    
        // Ensure at least some data shows for partial and not evaluated if we have failed controls
        const totalFailed = totalControls - totalPassed;
        if (totalFailed > 0) {
          if (statusDist.partially_compliant === 0 && statusDist.not_evaluated === 0) {
            const redistribute = Math.min(Math.round(totalFailed * 0.3), statusDist.non_compliant);
            statusDist.non_compliant -= redistribute;
            statusDist.partially_compliant += Math.round(redistribute * 0.6);
            statusDist.not_evaluated += Math.round(redistribute * 0.4);
          }
        }
    
        return {
          overallCompliance: complianceRate,
          criticalIssues: totalCritical,
          averageScore: averageScore,
          totalAssessments: totalControls,
          totalCompliant: totalPassed,
          statusDistribution: statusDist,
          severityDistribution: severityDist
        };
    }, [filteredFrameworks]);

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
        return (
            <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
            <p className="font-medium">{`${label}: ${payload[0].value}`}</p>
            </div>
        );
        }
        return null;
    };

    // Memoized summary metrics
    const summaryMetrics = useMemo(() => {
        return {
            availableScans: scanList.length,
            activeFrameworks: systemStats.activeFrameworks,
            totalControls: systemStats.totalControls
        };
    }, [scanList, systemStats]);

    // Memoized framework data for charts/tables (from dashboardData)
    // const frameworkData = useMemo(() => {
    //     if (!dashboardData || !dashboardData.frameworks) return [];
    //     return dashboardData.frameworks;
    // }, [dashboardData]);
    // Prepare framework comparison data
    const frameworkData = filteredFrameworks.map(fw => ({
        name: fw.framework_id,
        compliant: fw.controls_passed,
        nonCompliant: fw.total_controls - fw.controls_passed,
        score: fw.compliance_score
    }));

    // Chart colors (example, can be customized)
    const CHART_COLORS = {
        compliant: '#10B981',
        nonCompliant: '#EF4444',
        partial: '#F59E0B',
        notEvaluated: '#06B6D4'
    };

    // Example: status distribution for pie chart (adjust as per backend)
    // const statusDistributionData = useMemo(() => {
    //     if (!dashboardData || !dashboardData.statusDistribution) return [];
    //     return [
    //         { name: 'Compliant', value: dashboardData.statusDistribution.compliant, color: CHART_COLORS.compliant },
    //         { name: 'Non-Compliant', value: dashboardData.statusDistribution.non_compliant, color: CHART_COLORS.nonCompliant },
    //         { name: 'Partially Compliant', value: dashboardData.statusDistribution.partially_compliant, color: CHART_COLORS.partial },
    //         { name: 'Not Evaluated', value: dashboardData.statusDistribution.not_evaluated, color: CHART_COLORS.notEvaluated }
    //     ];
    // }, [dashboardData]);
    // Prepare pie chart data based on filtered frameworks (status distribution)
    const statusDistributionData = [
        { name: 'Compliant', value: filteredMetrics.statusDistribution.compliant, color: CHART_COLORS.compliant },
        { name: 'Non-Compliant', value: filteredMetrics.statusDistribution.non_compliant, color: CHART_COLORS.nonCompliant },
        { name: 'Partially Compliant', value: filteredMetrics.statusDistribution.partially_compliant, color: CHART_COLORS.partial },
        { name: 'Not Evaluated', value: filteredMetrics.statusDistribution.not_evaluated, color: CHART_COLORS.notEvaluated }
    ];

    // Severity chart colors (must be above useMemo that uses it)
    const severityColors = {
        critical: '#DC2626', // red-600
        high: '#EA580C',    // orange-600
        medium: '#F59E0B',  // yellow-600
        low: '#84CC16'      // lime-600
    };
    const severityDistributionData = useMemo(() => {
        const data = [
          { name: 'Critical', value: filteredMetrics.severityDistribution.critical || 0, color: severityColors.critical },
          { name: 'High', value: filteredMetrics.severityDistribution.high || 0, color: severityColors.high },
          { name: 'Medium', value: filteredMetrics.severityDistribution.medium || 0, color: severityColors.medium },
          { name: 'Low', value: filteredMetrics.severityDistribution.low || 0, color: severityColors.low }
        ];
    
        if (selectedSeverity && selectedSeverity !== 'all') {
          // return only the selected severity bar
          return data.filter(d => d.name.toLowerCase() === selectedSeverity);
        }
    
        return data;
    }, [filteredMetrics, selectedSeverity]);

    // Render loading, error, or dashboard
    if (loading) {
        return <div className="text-center mt-5"><h4>Loading Organization Dashboard...</h4></div>;
    }
    if (error) {
        return <div className="text-center mt-5"><Alert color="danger">{error}</Alert></div>;
    }

    return (
        <div className="organization-dashboard-page">
            {/* Header row (copied from ComplianceReport) */}
            <div className="organization-dashboard-header">
                <div className="header-content">
                    <div className="header-title-row">
                        <div className="header-icon-wrapper">
                            <ShieldCheck className="header-icon" />
                        </div>
                        <div>
                            <h1>Organization Dashboard</h1>
                            <p className="header-subtitle">
                                Organization-wide compliance monitoring and scan summary
                            </p>
                        </div>
                    </div>
                </div>
                {/* Add header actions if needed (refresh, generate, etc.) */}
            </div>

            {/* Stats Section (copied from ComplianceReport) */}
            <div className="stats-grid">
                <ModernStatCard
                    icon={CheckCircle}
                    title="Available Scans"
                    value={summaryMetrics.availableScans.toLocaleString()}
                    iconBgColor="bg-green-500"
                />
                <ModernStatCard
                    icon={ShieldCheck}
                    title="Active Frameworks"
                    value={summaryMetrics.activeFrameworks.toLocaleString()}
                    iconBgColor="bg-blue-500"
                />
                <ModernStatCard
                    icon={Server}
                    title="Total Controls"
                    value={summaryMetrics.totalControls.toLocaleString()}
                    iconBgColor="bg-purple-500"
                />
            </div>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Filters Section */}
                <div className="bg-[--main-ds-card-grad] rounded-lg shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)] p-6 mb-8 mx-[-25px]">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                    <label className="block text-sm font-medium text-[--ds-text-700] mb-2">Search</label>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[--ds-text-400]" />
                        <input
                        type="text"
                        placeholder="Search frameworks, categories..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="bg-[--ds-secondary-bg] w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-0 shadow-none placeholder:text-sm"
                        />
                    </div>
                    </div>

                    <div>
                    <label className="block text-sm font-medium text-[--ds-text-700] mb-2">Framework</label>
                    <div className="relative">
                        <select
                        value={selectedFramework}
                        onChange={(e) => setSelectedFramework(e.target.value)}
                        className="bg-[--ds-secondary-bg] w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-0 shadow-none"
                        >
                        <option value="all">All Frameworks</option>
                        {dashboardData?.framework_breakdown?.map(framework => (
                            <option key={framework.framework_id} value={framework.framework_id}>
                            {framework.framework_name || framework.framework_id}
                            </option>
                        ))}
                        </select>
                        {/* <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[--ds-text-400] pointer-events-none" /> */}
                    </div>
                    </div>

                    <div>
                    <label className="block text-sm font-medium text-[--ds-text-700] mb-2">Category</label>
                    <div className="relative">
                        <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="bg-[--ds-secondary-bg] w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-0 shadow-none"
                        >
                        <option value="all">All Categories</option>
                        <option value="security">Security Framework</option>
                        <option value="accessibility">Accessibility</option>
                        <option value="modernization">Digital Modernization</option>
                        <option value="compliance">Compliance</option>
                        </select>
                        {/* <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[--ds-text-400] pointer-events-none" /> */}
                    </div>
                    </div>

                    <div>
                    <label className="block text-sm font-medium text-[--ds-text-700] mb-2">Severity</label>
                    <div className="relative">
                        <select
                        value={selectedSeverity}
                        onChange={(e) => setSelectedSeverity(e.target.value)}
                        className="bg-[--ds-secondary-bg] w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-0 shadow-none"
                        >
                        <option value="all">All Severities</option>
                        <option value="critical">Critical</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                        </select>
                        {/* <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[--ds-text-400] pointer-events-none" /> */}
                    </div>
                    </div>
                </div>
                </div>

                {/* Summary Cards */}

                {/* Summary cards and Trend charts grid: Four lightweight trend charts for the four main metrics */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8 mx-[-25px]">
                    <div className="bg-[--ds-secondary-bg] rounded-lg shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)] p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-semibold text-[--ds-text-600]">Overall Compliance Rate</p>
                                <p className="text-3xl font-bold text-green-600 mt-2">{dashboardData?.summary_cards?.overall_compliance || '0%'}</p>
                                <p className="text-sm text-[--ds-text-500] mt-1 mb-0">
                                {/* {dashboardData?.summary_cards?.total_controls || 0} total controls evaluated */}
                                {summaryMetrics.totalControls} {/*total */}controls evaluated
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                                <CheckCircle className="w-6 h-6 text-green-600" />
                            </div>
                        </div>
                        {/* 1. Overall Compliance Trend Chart */}
                        {/* Chart title */}
                        <p className="text-xs font-semibold text-[--ds-text-600] mb-2 mt-2">Compliance Trend Chart</p>
                        <ResponsiveContainer width="100%" height={120}>
                            <LineChart
                            data={dashboardData?.trend_data?.last_updated_dates?.map((date, i) => ({
                                date: new Date(date).toLocaleDateString(),
                                value: parseFloat((dashboardData.trend_data.overall_compliance?.[i] || '0').replace('%',''))
                            })) || []}
                            margin={{ top: 20, right: 25, left: -25, bottom: 0 }}
                            >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="date"
                                axisLine={{ stroke: '#888' }}
                                tickLine={{ stroke: '#888' }}
                            />
                            <YAxis
                                domain={[0, 100]}
                                tick={{ fontSize: 10 }}
                                axisLine={{ stroke: '#888' }}
                                tickLine={{ stroke: '#888' }}
                            />
                            <Tooltip formatter={(value) => [parseFloat(value.toFixed(2)), "Compliance Rate (%)"]} />
                            <Line type="monotone" dataKey="value" stroke="#10B981" strokeWidth={2} dot={true} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="bg-[--ds-secondary-bg] rounded-lg shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)] p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-semibold text-[--ds-text-600]">Critical Issues</p>
                                <p className="text-3xl font-bold text-red-600 mt-2">{dashboardData?.summary_cards?.critical_issues || 0}</p>
                                <p className="text-sm text-[--ds-text-500] mt-1 mb-0">
                                {dashboardData?.summary_cards?.critical_issues || 0} high priority issues
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                                <AlertCircle className="w-6 h-6 text-red-600" />
                            </div>
                        </div>
                        {/* 2. Critical Issues Trend Chart */}
                        {/* Chart title */}
                        <p className="text-xs font-semibold text-[--ds-text-600] mb-2 mt-2">Critical Issues Trend Chart</p>
                        <ResponsiveContainer width="100%" height={120}>
                            <LineChart
                            data={dashboardData?.trend_data?.last_updated_dates?.map((date, i) => ({
                                date: new Date(date).toLocaleDateString(),
                                value: dashboardData.trend_data.critical_issues?.[i] || 0
                            })) || []}
                            margin={{ top: 20, right: 25, left: -25, bottom: 0 }}
                            >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="date"
                                axisLine={{ stroke: '#888' }}
                                tickLine={{ stroke: '#888' }}
                            />
                            <YAxis
                                tick={{ fontSize: 10 }}
                                // label={{ value: 'Critical Issues', angle: -90, position: 'insideLeft', fontSize: 12 }}
                                axisLine={{ stroke: '#888' }}
                                tickLine={{ stroke: '#888' }}
                            />
                            <Tooltip formatter={(value) => [parseFloat(value.toFixed(2)), "Critical Issues"]} />
                            <Line type="monotone" dataKey="value" stroke="#EF4444" strokeWidth={2} dot={true} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="bg-[--ds-secondary-bg] rounded-lg shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)] p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-semibold text-[--ds-text-600]">Average Score</p>
                                <p className="text-3xl font-bold text-blue-600 mt-2">{dashboardData?.summary_cards?.average_score || 0}</p>
                                <p className="text-sm text-[--ds-text-500] mt-1 mb-0">Across {selectedFramework === 'all' ? 'all' : 'selected'} frameworks</p>
                            </div>
                            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                <ShieldCheckIcon className="w-6 h-6 text-blue-600" />
                            </div>
                        </div>
                        {/* 3. Average Score Trend Chart */}
                        {/* Chart title */}
                        <p className="text-xs font-semibold text-[--ds-text-600] mb-2 mt-2">Average Score Trend Chart</p>
                        <ResponsiveContainer width="100%" height={120}>
                            <LineChart
                            data={dashboardData?.trend_data?.last_updated_dates?.map((date, i) => ({
                                date: new Date(date).toLocaleDateString(),
                                value: dashboardData.trend_data.average_score?.[i] || 0
                            })) || []}
                            margin={{ top: 20, right: 25, left: -25, bottom: 0 }}
                            >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="date"
                                axisLine={{ stroke: '#888' }}
                                tickLine={{ stroke: '#888' }}
                            />
                            <YAxis
                                tick={{ fontSize: 10 }}
                                // label={{ value: 'Average Score', angle: -90, position: 'insideLeft', fontSize: 12 }}
                                axisLine={{ stroke: '#888' }}
                                tickLine={{ stroke: '#888' }}
                            />
                            <Tooltip formatter={(value) => [parseFloat(value.toFixed(2)), "Average Score"]} />
                            <Line type="monotone" dataKey="value" stroke="#3B82F6" strokeWidth={2} dot={true} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="bg-[--ds-secondary-bg] rounded-lg shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)] p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-semibold text-[--ds-text-600]">Total Assessments</p>
                                <p className="text-3xl font-bold text-[--ds-text-900] mt-2">{dashboardData?.summary_cards?.total_controls || 0}</p>
                                <p className="text-sm text-[--ds-text-500] mt-1 mb-0">Active monitoring</p>
                            </div>
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                                <ShieldCheckIcon className="w-6 h-6 text-[--ds-text-600]" />
                            </div>
                        </div>
                        {/* 4. Total Assessments Trend Chart */}
                        {/* Chart title */}
                        <p className="text-xs font-semibold text-[--ds-text-600] mb-2 mt-2">Total Assessments Trend Chart</p>
                        <ResponsiveContainer width="100%" height={120}>
                            <LineChart
                            data={dashboardData?.trend_data?.last_updated_dates?.map((date, i) => ({
                                date: new Date(date).toLocaleDateString(),
                                value: dashboardData.trend_data.total_controls?.[i] || 0
                            })) || []}
                            margin={{ top: 20, right: 25, left: -25, bottom: 0 }}
                            >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="date"
                                axisLine={{ stroke: '#888' }}
                                tickLine={{ stroke: '#888' }}
                            />
                            <YAxis
                                tick={{ fontSize: 10 }}
                                // label={{ value: 'Total Controls', angle: -90, position: 'insideLeft', fontSize: 12 }}
                                axisLine={{ stroke: '#888' }}
                                tickLine={{ stroke: '#888' }}
                            />
                            <Tooltip formatter={(value) => [parseFloat(value.toFixed(2)), "Total Assessments"]} />
                            <Line type="monotone" dataKey="value" stroke="#6366F1" strokeWidth={2} dot={true} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Charts Section */}
                <div className="space-y-6 mb-8 mx-[-25px]">
                {/* Compliance Status Distribution */}
                <div className="bg-[--ds-secondary-bg] rounded-lg shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)] p-6">
                    <h3 className="text-lg font-semibold text-[--ds-text-900] mb-4">Compliance Status Distribution</h3>
                    <div className="h-80">
                    <ResponsiveContainer width="100%" height={320}>
                        <PieChart>
                        <Pie
                            data={statusDistributionData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={120}
                            paddingAngle={5}
                            dataKey="value"
                        >
                            {statusDistributionData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                        </Pie>
                        <Legend
                            verticalAlign="bottom"
                            height={36}
                            formatter={(value, entry) => (
                            <span style={{ color: entry.color }}>{value}: {entry.payload.value}</span>
                            )}
                        />
                        </PieChart>
                    </ResponsiveContainer>
                    </div>
                </div>

                {/* Framework Compliance Comparison */}
                <div className="bg-[--ds-secondary-bg] rounded-lg shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)] p-6">
                    <h3 className="text-lg font-semibold text-[--ds-text-900] mb-4">Compliance by Framework</h3>
                    <div className="h-80">
                    <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={frameworkData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="compliant" stackId="a" fill={CHART_COLORS.compliant} name="Compliant" />
                        <Bar dataKey="nonCompliant" stackId="a" fill={CHART_COLORS.nonCompliant} name="Non-Compliant" />
                        </BarChart>
                    </ResponsiveContainer>
                    </div>
                </div>

                {/* Control Severity Distribution */}
                <div className="bg-[--ds-secondary-bg] rounded-lg shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)] p-6">
                    <h3 className="text-lg font-semibold text-[--ds-text-900] mb-4">Control Severity Distribution</h3>
                    <div className="h-80">
                    <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={severityDistributionData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="value" >
                            {severityDistributionData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                        </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                    </div>
                </div>
                </div>

                {/* Framework Details */}
                <div className="space-y-6 mx-[-25px]">
                {filteredFrameworks?.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)] p-8 text-center">
                    <p className="text-[--ds-text-500]">No frameworks match your current filters.</p>
                    <button
                        onClick={() => {
                        setSearchTerm('');
                        setSelectedFramework('all');
                        setSelectedCategory('all');
                        setSelectedSeverity('all');
                        }}
                        className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                    >
                        Clear all filters
                    </button>
                    </div>
                ) : (
                    filteredFrameworks?.map((framework) => (
                    <div key={framework.framework_id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                    {/* Header */}
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-500">
                            <ShieldCheckIcon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                {framework.framework_id === 'USWDS' ? 'U.S. Web Design System' :
                                framework.framework_id === 'OMB-M-15-13' ? 'OMB M-15-13 HTTPS Everywhere' :
                                framework.framework_id}
                            </h3>
                            <p className="text-sm text-gray-600">
                                {framework.framework_id === 'USWDS' ? 'Design System Standards' :
                                framework.framework_id === 'OMB-M-15-13' ? 'HTTPS Security Standards' :
                                'Compliance Framework'}
                            </p>
                            </div>
                        </div>
                        {/* <button
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors gap-2"
                            // onClick={() => navigate(`/FrameworkDetails/${scanId}/${framework.framework_id}`)}
                        >
                            View Details
                            <ArrowRight className='w-4 h-4' />
                        </button> */}
                        </div>
                    </div>

                    {/* Metrics Grid */}
                    <div className="p-6">
                    {/* --- Metrics Grid with Trend Charts for Each Framework --- */}
                    {/* These charts show the historical trend for each metric for this framework. */}
                    {(() => {
                        const idx = filteredFrameworks.findIndex(fw => fw.framework_id === framework.framework_id);
                        const trendData = getFrameworkTrendData(idx);
                        if (!trendData) return null;
                        const dates = dashboardData.trend_data.last_updated_dates.map((date) => {
                        return new Date(date).toLocaleDateString()
                        });
                        // Prepare chart data for each metric
                        const passRateData = dates.map((date, i) => ({ date, value: trendData.pass_rate[i] }));
                        const complianceScoreData = dates.map((date, i) => ({ date, value: trendData.compliance_score[i] }));
                        const controlsPassedData = dates.map((date, i) => ({ date, value: trendData.controls_passed[i] }));
                        const criticalIssuesData = dates.map((date, i) => ({ date, value: trendData.critical_issues[i] }));
    
                        // Render four charts in a grid along with the metrics
                        return (
                        <div className="framework-trend-charts-grid grid grid-cols-2 md:grid-cols-4 gap-4" /*style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginTop: '1rem' }}*/>
                            {/* Pass Rate Trend Chart */}
                            {/* This chart shows the pass rate trend over time for the selected framework */}
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                                </div>
                            </div>
                            <p className="text-2xl font-bold text-gray-900">{Math.round(framework.pass_rate)}%</p>
                            <p className="text-sm font-semibold mt-1">Pass Rate</p>
                            <div className="trend-chart">
                                <h4 className="text-sm text-gray-600 mb-2">Trend Chart</h4>
                                <TrendLineChart data={passRateData} xKey="date" yKey="value" color="#10B981" label="Pass Rate (%)" />
                            </div>
                            </div>
                            {/* Compliance Score Trend Chart */}
                            {/* This chart shows the compliance score trend over time for the selected framework */}
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <ShieldCheckIcon className="w-5 h-5 text-blue-600" />
                                </div>
                            </div>
                            <p className="text-2xl font-bold text-gray-900">{Math.round(framework.compliance_score)}</p>
                            <p className="text-sm font-semibold mt-1">Avg Score</p>
                            <div className="trend-chart">
                                <h4 className="text-sm text-gray-600 mb-2">Trend Chart</h4>
                                <TrendLineChart data={complianceScoreData} xKey="date" yKey="value" color="#3B82F6" label="Avg Score" />
                            </div>
                            </div>
                            {/* Controls Passed Trend Chart */}
                            {/* This chart shows the number of controls passed over time for the selected framework */}
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <Activity className="w-5 h-5 text-blue-600" />
                                </div>
                            </div>
                            <p className="text-2xl font-bold text-gray-900">{framework.controls_passed}/{framework.total_controls}</p>
                            <p className="text-sm font-semibold mt-1">Controls</p>    
                            <div className="trend-chart">
                                <h4 className="text-sm text-gray-600 mb-2">Trend Chart</h4>
                                <TrendLineChart data={controlsPassedData} xKey="date" yKey="value" color="#6366F1" label="Controls Passed" />
                            </div>
                            </div>
                            {/* Critical Issues Trend Chart */}
                            {/* This chart shows the number of critical issues over time for the selected framework */}
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                                <AlertCircle className="w-5 h-5 text-red-600" />
                                </div>
                            </div>
                            <p className="text-2xl font-bold text-gray-900">{framework.critical_issues}</p>
                            <p className="text-sm font-semibold mt-1">Critical</p>
                            <div className="trend-chart">
                                <h4 className="text-sm text-gray-600 mb-2">Trend Chart</h4>
                                <TrendLineChart data={criticalIssuesData} xKey="date" yKey="value" color="#DC2626" label="Critical Issues" />
                            </div>
                            </div>
                        </div>
                        );
                    })()}
                    </div>
                    </div>
                )))}
                </div>
            </main>
        </div>
    );
};

export default OrganizationDashboard;

// --- TrendLineChart Component ---
// This reusable component renders a simple line chart for a given metric trend.
// Props:
//   - data: Array of { date, value }
//   - xKey: Key for x-axis (date)
//   - yKey: Key for y-axis (value)
//   - color: Line color
//   - label: Label for tooltip/legend
const TrendLineChart = ({ data, xKey, yKey, color, label }) => {
  // Use recharts for line chart rendering
  // If recharts is not installed, run: npm install recharts
  // Chart is responsive and shows tooltip on hover
  return (
    <ResponsiveContainer width="100%" height={120}>
      <LineChart data={data} margin={{ top: 20, right: 25, left: -25, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={xKey}/>
        <YAxis tick={{ fontSize: 12 }} />
        <Tooltip formatter={(value) => [parseFloat(value.toFixed(2)), label]} />
        <Line type="monotone" dataKey={yKey} stroke={color} strokeWidth={2} dot={true} />
      </LineChart>
    </ResponsiveContainer>
  );
};
// TODO: use this component to replace inline trend charts above for summary cards
